import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { StickyNote, CalendarDays, X } from 'lucide-react';

/**
 * Scheduler list ▸ Notes column (AJ 2026-10-20; SCHEDULER_ROLE_PAGE.md §Notes).
 * Two notes per row:
 *   • Notes — OL only, several paragraphs, never sent to the website or Google Calendar.
 *   • Google Calendar note — short (CAL_MAX), the ONLY note text added to the row's
 *     Google Calendar event(s).
 * The cell shows the notes clipped to one line (calendar icon when a calendar note is set);
 * hovering shows both in full; clicking opens the editor (add / edit / delete, Save / Cancel).
 * `onSave({notes?, calendar_notes?})` sends only what changed through the list's inline PATCH.
 */
const MAX = 20000;
const CAL_MAX = 500;

export default function ScheduleNotesCell({ value, calendarNote, readOnly, isDark, title, onSave, testId }) {
  const text = value || '';
  const cal = calendarNote || '';
  const [hover, setHover] = useState(null);
  const [editing, setEditing] = useState(false);
  const timer = useRef(null);
  const cellRef = useRef(null);

  const showHover = () => {
    if ((!text && !cal) || editing) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const r = cellRef.current?.getBoundingClientRect();
      if (!r) return;
      const w = 380;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
      const below = r.bottom + 6;
      const top = below + 280 > window.innerHeight ? Math.max(8, r.top - 286) : below;
      setHover({ top, left, w });
    }, 250);
  };
  const hideHover = () => { clearTimeout(timer.current); setHover(null); };
  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <>
      <div ref={cellRef} onMouseEnter={showHover} onMouseLeave={hideHover}
        onClick={() => { if (!readOnly) { hideHover(); setEditing(true); } }}
        className={`flex items-center gap-1 text-xs ${readOnly ? '' : 'cursor-pointer'}`}
        data-testid={testId} aria-label={text || cal ? 'Notes — hover to read, click to edit' : 'Add notes'}>
        {cal && <CalendarDays className="h-3 w-3 shrink-0 text-sky-500" aria-label="Has a Google Calendar note" />}
        <span className={`truncate ${text ? (isDark ? 'text-slate-200' : 'text-slate-700') : (isDark ? 'text-slate-600' : 'text-slate-300')}`}>
          {text ? text.replace(/\s*\n+\s*/g, ' · ') : (cal ? cal : (readOnly ? '' : '+ notes'))}
        </span>
      </div>
      {hover && createPortal(
        <div style={{ position: 'fixed', top: hover.top, left: hover.left, width: hover.w, zIndex: 60 }}
          className={`rounded-lg border shadow-xl p-3 text-xs max-h-72 overflow-auto pointer-events-none
            ${isDark ? 'bg-slate-900 border-slate-600 text-slate-100' : 'bg-white border-slate-200 text-slate-800'}`}
          data-testid={`${testId}-hover`}>
          {title && <div className="font-semibold mb-1">{title}</div>}
          {text && <div className="whitespace-pre-wrap">{text}</div>}
          {cal && (
            <div className={`mt-2 pt-2 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
              <div className="flex items-center gap-1 font-medium text-sky-600 dark:text-sky-400"><CalendarDays className="h-3 w-3" /> Google Calendar note</div>
              <div className="whitespace-pre-wrap">{cal}</div>
            </div>
          )}
        </div>, document.body)}
      {editing && <NotesDialog text={text} cal={cal} title={title} isDark={isDark} testId={testId}
        onCancel={() => setEditing(false)}
        onSave={async (fields) => { await onSave(fields); setEditing(false); }} />}
    </>
  );
}

function NotesDialog({ text, cal, title, isDark, testId, onCancel, onSave }) {
  const [v, setV] = useState(text);
  const [c, setC] = useState(cal);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const changed = {};
  if (v !== text) changed.notes = v.trim() ? v : '';
  if (c.trim() !== cal.trim()) changed.calendar_notes = c.trim();
  const save = async (fields) => {
    if (!Object.keys(fields).length) { onCancel(); return; }
    setBusy(true); setErr('');
    try { await onSave(fields); } catch (e) { setErr(e?.response?.data?.detail || 'Could not save the notes'); setBusy(false); }
  };
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onCancel]);
  const field = `w-full rounded-md border p-2 text-sm leading-relaxed ${isDark ? 'bg-slate-800 border-slate-600 text-slate-100' : 'bg-white border-slate-300 text-slate-900'}`;
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center bg-black/40 p-4" onClick={onCancel}>
      <div className={`mt-12 w-full max-w-2xl rounded-xl border shadow-xl ${isDark ? 'bg-slate-900 border-slate-700 text-slate-100' : 'bg-white border-slate-200 text-slate-900'}`}
        onClick={(e) => e.stopPropagation()} data-testid={`${testId}-dialog`}>
        <div className={`flex items-center gap-2 px-4 py-3 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          <StickyNote className="h-4 w-4 text-amber-500" />
          <h3 className="font-semibold truncate">Notes{title ? ` — ${title}` : ''}</h3>
          <button type="button" onClick={onCancel} className="ml-auto text-slate-400 hover:text-slate-700 dark:hover:text-white" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="space-y-1">
            <div className="flex items-baseline gap-2 text-xs">
              <span className="font-semibold">Notes</span>
              <span className="text-slate-500">OL only — never on the website or Google Calendar</span>
              <span className="ml-auto text-slate-400">{v.length.toLocaleString()} / {MAX.toLocaleString()}</span>
            </div>
            <textarea autoFocus rows={11} maxLength={MAX} value={v} onChange={(e) => setV(e.target.value)}
              placeholder="Notes for this row — as many paragraphs as you need."
              className={`${field} resize-y`} data-testid={`${testId}-text`} />
          </div>
          <div className="space-y-1">
            <div className="flex items-baseline gap-2 text-xs">
              <span className="font-semibold inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5 text-sky-500" /> Google Calendar note</span>
              <span className="text-slate-500">short — added to this row's Google Calendar event</span>
              <span className={`ml-auto ${c.length > CAL_MAX - 50 ? 'text-amber-600' : 'text-slate-400'}`}>{c.length} / {CAL_MAX}</span>
            </div>
            <textarea rows={2} maxLength={CAL_MAX} value={c} onChange={(e) => setC(e.target.value)}
              placeholder="e.g. Wear white jerseys · Arrive 45 min early"
              className={`${field} resize-none`} data-testid={`${testId}-cal`} />
          </div>
          {err && <div className="text-xs text-red-600">{err}</div>}
        </div>
        <div className={`flex items-center gap-2 px-4 py-3 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          {(text || cal) && (
            <button type="button" disabled={busy}
              onClick={() => { if (window.confirm('Delete both notes for this row?')) save({ ...(text ? { notes: '' } : {}), ...(cal ? { calendar_notes: '' } : {}) }); }}
              className="text-xs text-red-600 hover:underline disabled:opacity-50" data-testid={`${testId}-delete`}>Delete notes</button>
          )}
          <span className="flex-1" />
          <button type="button" onClick={onCancel} disabled={busy}
            className={`rounded-md px-3 py-1.5 text-sm border ${isDark ? 'border-slate-600 text-slate-200' : 'border-slate-300 text-slate-700'}`}
            data-testid={`${testId}-cancel`}>Cancel</button>
          <button type="button" onClick={() => save(changed)} disabled={busy || !Object.keys(changed).length}
            className="rounded-md px-3 py-1.5 text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
            data-testid={`${testId}-save`}>Save</button>
        </div>
      </div>
    </div>, document.body);
}
